import { Suspense, lazy, useCallback, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import {
  Button,
  Chip,
  Drawer,
  IconButton,
  Typography,
} from '@material-ui/core';

import {
  RiArrowDownLine,
  RiArrowRightLine,
  RiArrowUpLine,
  RiCloseLine,
  RiFullscreenExitLine,
  RiFullscreenLine,
} from '@remixicon/react';

import { useRouteRef } from '@backstage/core-plugin-api';

import { extractLastNameComponent } from '@spotify/confidence-core-react';
import {
  METRIC_DISPLAY,
  RecordingAnalysis,
  getMetric,
  hasAnalysisContent,
  useRecording,
} from '@spotify/confidence-recordings-react';

import { recordingRouteRef } from '../routes';
import classes from './RecordingDrawer.module.css';
import { getRecordingLabel } from './types';
import type { RecordingHit } from './types';

const LazyRecordingPlayer = lazy(() =>
  import('@spotify/confidence-recordings-react').then(m => ({
    default: m.RecordingPlayer,
  })),
);

const ICON_SIZE = 16;

const SIGNAL_METRIC_KEYS = ['rageClicks', 'deadClicks', 'scrollBacks'];

type RecordingDetailsProps = {
  recordingName: string;
};

const RecordingDetails = ({ recordingName }: RecordingDetailsProps) => {
  const { recording } = useRecording({ name: recordingName });

  const signals = useMemo(() => {
    if (!recording?.metrics) return [];
    return SIGNAL_METRIC_KEYS.flatMap(key => {
      const display = METRIC_DISPLAY[key];
      if (!display) return [];
      const value = getMetric(recording.metrics, key);
      return value && value > 0
        ? [{ key, label: `${value} ${display.label}` }]
        : [];
    });
  }, [recording?.metrics]);

  const routes = recording?.routing?.routes ?? [];

  if (!recording) return null;

  return (
    <>
      {hasAnalysisContent(recording.analysis) && (
        <RecordingAnalysis analysis={recording.analysis} />
      )}
      {signals.length > 0 && (
        <div className={classes.section}>
          <Typography
            variant="body2"
            color="textSecondary"
            className={classes.sectionTitle}
          >
            Signals
          </Typography>
          <div className={classes.signals}>
            {signals.map(signal => (
              <Chip key={signal.key} label={signal.label} size="small" />
            ))}
          </div>
        </div>
      )}
      {routes.length > 0 && (
        <div className={classes.section}>
          <Typography
            variant="body2"
            color="textSecondary"
            className={classes.sectionTitle}
          >
            Journey
          </Typography>
          <div className={classes.journey}>
            {routes.map((route, i) => (
              <Typography key={i} variant="body2" className={classes.route}>
                {route.pageUri}
              </Typography>
            ))}
          </div>
        </div>
      )}
    </>
  );
};

type RecordingDrawerProps = {
  recordings: RecordingHit[];
  activeIndex: number | null;
  onClose: () => void;
  onNavigate: (index: number) => void;
};

export const RecordingDrawer = ({
  recordings,
  activeIndex,
  onClose,
  onNavigate,
}: RecordingDrawerProps) => {
  const recordingRoute = useRouteRef(recordingRouteRef);
  const isOpen = activeIndex !== null;
  const recording = activeIndex !== null ? recordings[activeIndex] : null;
  const recordingId = extractLastNameComponent(recording?.name) ?? '';
  const hasPrevious = activeIndex !== null && activeIndex > 0;
  const hasNext = activeIndex !== null && activeIndex < recordings.length - 1;
  const [fullWidth, setFullWidth] = useState(false);
  const [entered, setEntered] = useState(false);

  const handleClose = useCallback(() => {
    setFullWidth(false);
    setEntered(false);
    onClose();
  }, [onClose]);

  return (
    <Drawer
      variant="temporary"
      anchor="right"
      open={isOpen}
      onClose={handleClose}
      SlideProps={{
        unmountOnExit: true,
        onEntered: () => setEntered(true),
        onExiting: () => setEntered(false),
      }}
      ModalProps={{ keepMounted: false }}
      PaperProps={{
        className: entered ? classes.drawerEntered : undefined,
        style: { width: fullWidth ? '100vw' : 680 },
      }}
    >
      {recording && (
        <>
          <div className={classes.header}>
            <Typography variant="subtitle1" className={classes.title} noWrap>
              {getRecordingLabel(recording)}
            </Typography>
            <div className={classes.headerActions}>
              <IconButton
                size="small"
                className={classes.fullWidthButton}
                onClick={() => setFullWidth(prev => !prev)}
                aria-label={
                  fullWidth ? 'Exit full width' : 'Expand to full width'
                }
              >
                {fullWidth ? (
                  <RiFullscreenExitLine size={ICON_SIZE} />
                ) : (
                  <RiFullscreenLine size={ICON_SIZE} />
                )}
              </IconButton>
              <IconButton
                size="small"
                disabled={!hasPrevious}
                onClick={() => onNavigate(activeIndex! - 1)}
                aria-label="Previous recording"
              >
                <RiArrowUpLine size={ICON_SIZE} />
              </IconButton>
              <IconButton
                size="small"
                disabled={!hasNext}
                onClick={() => onNavigate(activeIndex! + 1)}
                aria-label="Next recording"
              >
                <RiArrowDownLine size={ICON_SIZE} />
              </IconButton>
              <IconButton
                size="small"
                onClick={handleClose}
                aria-label="Close recording preview"
              >
                <RiCloseLine size={ICON_SIZE} />
              </IconButton>
            </div>
          </div>
          <div className={classes.content}>
            <div className={classes.playerContainer}>
              <Suspense fallback={<div className={classes.placeholder} />}>
                <LazyRecordingPlayer
                  key={recording.name}
                  src={recording.preview!}
                  variant="simplified"
                  autoplay={false}
                />
              </Suspense>
            </div>
            {!fullWidth && (
              <>
                <span className={classes.score}>
                  {(recording.score * 100).toFixed(1)}% relevance
                </span>
                <RecordingDetails recordingName={recording.name} />
                <Button
                  variant="outlined"
                  size="small"
                  component={Link}
                  to={recordingRoute({ id: recordingId })}
                  endIcon={<RiArrowRightLine size={14} />}
                  className={classes.goToRecording}
                >
                  Go to recording
                </Button>
              </>
            )}
          </div>
        </>
      )}
    </Drawer>
  );
};
